import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { RequestHMSupport } from '@/components/shared/RequestHMSupport';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { Target, Loader2, ArrowLeft, CircleHelp, Plus, Download, BellRing, History, CheckCircle2, TrendingUp, X, GraduationCap, ExternalLink } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';

// TalentCompetency — "Competencias: Esperado vs. Observado" (Client Portal V1).
// Administered by Client Admin / authorized HR; the evaluated employee NEVER
// completes this evaluation and it never appears in Mi Portal. The designated
// governed evaluator completes the behavioral observation. BMIC remains an
// CTH/CTH methodological asset (no client editing of the bank).
const STATUS_META = {
  green:  { es: 'Cumple / Supera', en: 'Meets / Exceeds', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  yellow: { es: 'Brecha moderada', en: 'Moderate Gap',    cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  red:    { es: 'Brecha prioritaria', en: 'Priority Gap', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  insufficient_evidence: { es: 'Evidencia insuficiente', en: 'Insufficient Evidence', cls: 'bg-slate-100 text-slate-600 border-slate-300 border-dashed' },
};

const SCALE = {
  1: { es: 'Muy por debajo de lo esperado', en: 'Well below expected' },
  2: { es: 'Por debajo de lo esperado', en: 'Below expected' },
  3: { es: 'Nivel esperado / adecuado', en: 'Expected / adequate level' },
  4: { es: 'Por encima de lo esperado', en: 'Above expected' },
  5: { es: 'Consistentemente por encima de lo esperado', en: 'Consistently above expected' },
};

const CADENCES = [
  { v: 'quarterly',  es: 'Trimestral', en: 'Quarterly' },
  { v: 'semiannual', es: 'Semestral',  en: 'Semiannual' },
  { v: 'annual',     es: 'Anual',      en: 'Annual' },
  { v: 'custom',     es: 'Personalizada (meses)', en: 'Custom (months)' },
];

const TREND_COLORS = ['#0f3d5c', '#b45309', '#1a7f4e', '#b91c1c', '#6d28d9', '#0e7490',
  '#a21caf', '#4d7c0f', '#9a3412', '#1d4ed8', '#be123c', '#115e59'];

export default function ClientExpectedVsObserved() {
  const { i18n } = useTranslation();
  const { user } = useApp();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);

  const [benchmarks, setBenchmarks] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [people, setPeople] = useState([]);
  const [positions, setPositions] = useState([]);
  const [autoProfileHint, setAutoProfileHint] = useState(false);
  const [compNames, setCompNames] = useState({});
  const [busy, setBusy] = useState(false);
  // create form
  const [empId, setEmpId] = useState('');
  const [rpId, setRpId] = useState('');
  const [evaluatorId, setEvaluatorId] = useState('');
  const [selectedCodes, setSelectedCodes] = useState([]);
  // detail
  const [openBm, setOpenBm] = useState(null);
  const [results, setResults] = useState(null);
  const [answers, setAnswers] = useState({});
  const [openDetail, setOpenDetail] = useState(null);
  // reminders
  const [reminders, setReminders] = useState({ items: [] });
  const [settings, setSettings] = useState(null);
  const [customMonths, setCustomMonths] = useState('');
  // gap trend (longitudinal, immutable snapshots)
  const [trend, setTrend] = useState(null);
  const [trendMeta, setTrendMeta] = useState(null);
  // A5 governed cancellation + A11 priorities selection
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [prioritySel, setPrioritySel] = useState([]);
  // TalentTrain handoff status for the open measurement + trend selector (3+)
  const [handoffs, setHandoffs] = useState([]);
  const [trendSel, setTrendSel] = useState([]);
  const navigate = useNavigate();

  const reload = () => {
    api.get('/admin/rb/benchmarks').then((r) => setBenchmarks(r.data)).catch(() => {});
    api.get('/client/role-profiles').then((r) => setProfiles(r.data)).catch(() => {});
    api.get('/client/users').then((r) => setPeople(r.data)).catch(() => {});
    api.get('/client/positions').then((r) => setPositions(r.data || [])).catch(() => {});
    api.get('/admin/rb/competencies').then((r) => {
      const m = {}; (r.data || []).forEach((c) => { m[c.code] = c; }); setCompNames(m);
    }).catch(() => {});
    api.get('/admin/rb/reminders').then((r) => setReminders(r.data)).catch(() => {});
    api.get('/admin/rb/reminders/settings').then((r) => {
      setSettings(r.data);
      if (r.data?.custom_months) setCustomMonths(String(r.data.custom_months));
    }).catch(() => {});
  };
  useEffect(reload, []);
  // A3 — notification deep link: /client/talentcompetency/evo?open={id}
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('open');
    if (id) openBenchmark(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activePeople = useMemo(
    () => people.filter((p) => !['inactive', 'disabled'].includes(p.status)
      && (p.employment_status || 'active') !== 'former'),
    [people],
  );
  const employees = useMemo(
    () => activePeople.filter((p) => p.role === 'evaluated' || p.role === 'client_admin' || p.is_employee),
    [activePeople],
  );
  const [designated, setDesignated] = useState([]);
  useEffect(() => {
    if (!empId) { setDesignated([]); return; }
    api.get(`/client/evaluator-grants/eligible/${empId}`)
      .then((r) => setDesignated(r.data.items || []))
      .catch(() => setDesignated([]));
  }, [empId]);

  // UAT A5 — when the employee has a linked formal position whose position is
  // unambiguously bound to ONE approved profile, preselect it. Never guess:
  // without a direct position→profile link the selection stays manual.
  useEffect(() => {
    setAutoProfileHint(false);
    if (!empId) return;
    const emp = people.find((p) => p.id === empId);
    const pos = emp?.position_id ? positions.find((p) => p.id === emp.position_id) : null;
    const rp = pos?.role_profile_id ? profiles.find((p) => p.id === pos.role_profile_id) : null;
    if (rp) { setRpId(rp.id); setAutoProfileHint(true); }
  }, [empId]); // eslint-disable-line react-hooks/exhaustive-deps
  const evaluators = useMemo(() => {
    const admins = activePeople.filter((p) => p.role === 'client_admin');
    const ids = new Set(admins.map((p) => p.id));
    return [...admins, ...designated.filter((d) => !ids.has(d.id))];
  }, [activePeople, designated]);
  const selectedProfile = useMemo(() => profiles.find((p) => p.id === rpId), [profiles, rpId]);
  const profileCodes = useMemo(
    () => (selectedProfile?.requirements || []).map((r) => r.competency_code),
    [selectedProfile],
  );
  useEffect(() => {
    // Preselect the profile's competencies when ≤12; larger profiles require
    // explicit prioritization — nothing is truncated silently.
    setSelectedCodes(profileCodes.length <= 12 ? profileCodes : []);
  }, [rpId]); // eslint-disable-line react-hooks/exhaustive-deps

  const compLabel = (code) => {
    const c = compNames[code];
    return c ? (isES ? (c.name_es || c.name) : c.name) : code;
  };

  const toggleCode = (code) => {
    setSelectedCodes((prev) => prev.includes(code)
      ? prev.filter((c) => c !== code)
      : (prev.length >= 12 ? prev : [...prev, code]));
  };

  const create = async () => {
    setBusy(true);
    try {
      const emp = employees.find((p) => p.id === empId);
      const { data } = await api.post('/admin/rb/benchmarks', {
        role_profile_id: rpId,
        evaluated_user_id: empId,
        evaluated_name: emp?.name || '',
        evaluator_user_id: evaluatorId,
        competency_codes: selectedCodes,
      });
      toast.success(L(`Evaluación iniciada — ${data.snapshot.item_ids.length} indicadores conductuales`,
        `Evaluation started — ${data.snapshot.item_ids.length} behavioral indicators`));
      setEmpId(''); setRpId(''); setEvaluatorId(''); setSelectedCodes([]);
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d.message || 'Error') : formatApiError(e));
    } finally { setBusy(false); }
  };

  const openBenchmark = async (id) => {
    setResults(null); setAnswers({}); setOpenDetail(null); setHandoffs([]); setPrioritySel([]);
    try {
      const { data } = await api.get(`/admin/rb/benchmarks/${id}`);
      setOpenBm(data);
      if (data.response) {
        const r = await api.get(`/admin/rb/benchmarks/${id}/results`);
        setResults(r.data);
        api.get(`/admin/rb/benchmarks/${id}/handoffs`).then((h) => setHandoffs(h.data || [])).catch(() => {});
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const refreshHandoffs = async (bid) => {
    try { const h = await api.get(`/admin/rb/benchmarks/${bid}/handoffs`); setHandoffs(h.data || []); } catch {}
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/rb/benchmarks/${openBm.id}/responses`, {
        evaluator_name: user?.name || '', answers,
      });
      toast.success(L('Observación registrada', 'Observation submitted'));
      await openBenchmark(openBm.id);
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const cancelEvaluation = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/rb/benchmarks/${openBm.id}/cancel`, { reason: cancelReason.trim() });
      toast.success(L('Evaluación cancelada (registrada en auditoría)', 'Evaluation cancelled (audited)'));
      setShowCancel(false); setCancelReason(''); setOpenBm(null); reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  // Idempotent BATCH handoff: new priorities are created, priorities already
  // in TalentTrain are skipped (never duplicated, never an all-or-nothing 400).
  const createHandoffs = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/rb/benchmarks/${results.benchmark_id}/handoff`, {
        competency_codes: prioritySel,
      });
      const c = data.summary?.created || 0;
      const s = data.summary?.skipped_existing || 0;
      const parts = [];
      if (c) parts.push(L(`${c} prioridad(es) transferida(s) a TalentTrain`, `${c} priorit${c === 1 ? 'y' : 'ies'} transferred to TalentTrain`));
      if (s) parts.push(L(`${s} ya estaba(n) en TalentTrain y no se duplicaron`, `${s} already in TalentTrain — not duplicated`));
      if (parts.length) {
        toast.success(parts.join(' · '), {
          description: L('Sin inscripción automática; TalentTrain gobierna la acción de desarrollo.',
            'No automatic enrollment; TalentTrain governs the development action.'),
        });
      }
      (data.rejected || []).forEach((rj) => toast.error(`${compLabel(rj.competency_code)}: ${rj.reason}`));
      await refreshHandoffs(results.benchmark_id);
      setPrioritySel([]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const handoffByCode = useMemo(() => {
    const m = {}; (handoffs || []).forEach((h) => { m[h.competency_code] = h; }); return m;
  }, [handoffs]);

  const downloadReport = async (fmt) => {
    try {
      const r = await api.get(`/admin/rb/benchmarks/${openBm.id}/export/${fmt}`, {
        params: { lang: isES ? 'es' : 'en' }, responseType: 'blob',
      });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      const m = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '');
      a.href = href; a.download = (m && m[1]) || `esperado-vs-observado.${fmt}`; a.click();
      URL.revokeObjectURL(href);
    } catch { toast.error(L('No se pudo descargar el informe.', 'Report download failed.')); }
  };

  const saveSettings = async (cadence, months) => {
    try {
      await api.put('/admin/rb/reminders/settings', {
        cadence, custom_months: cadence === 'custom' ? Number(months) : undefined,
      });
      toast.success(L('Frecuencia de reevaluación guardada', 'Re-evaluation cadence saved'));
      api.get('/admin/rb/reminders').then((r) => setReminders(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ackReminder = async (key) => {
    try {
      await api.post('/admin/rb/reminders/ack', { reminder_key: key });
      setReminders((prev) => ({
        ...prev,
        items: prev.items.map((i) => (i.reminder_key === key ? { ...i, acknowledged: true } : i)),
      }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const itemsByComp = useMemo(() => {
    if (!openBm) return [];
    const map = new Map();
    openBm.snapshot.items.forEach((it) => {
      if (!map.has(it.competency_code)) map.set(it.competency_code, []);
      map.get(it.competency_code).push(it);
    });
    return [...map.entries()];
  }, [openBm]);

  const answered = Object.keys(answers).length;
  const totalItems = openBm?.snapshot?.item_ids?.length || 0;
  const iAmEvaluator = openBm && openBm.evaluator_user_id === user?.id;
  const compName = (code) => {
    const n = openBm?.snapshot?.competency_names?.[code];
    return n ? (isES ? n.name_es : n.name) : compLabel(code);
  };
  const fmtDate = (v) => {
    if (!v) return '—';
    try {
      const d = new Date(v);
      const dd = String(d.getDate()).padStart(2, '0'), mm = String(d.getMonth() + 1).padStart(2, '0'), yy = d.getFullYear();
      return isES ? `${dd}/${mm}/${yy}` : `${mm}/${dd}/${yy}`;
    } catch { return v; }
  };
  const fmtTime = (v) => {
    try { const d = new Date(v); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; }
    catch { return ''; }
  };
  const statusBadge = (row) => {
    const m = STATUS_META[row.status];
    return (
      <Badge variant="outline" className={`text-xs ${m.cls}`} data-testid={`evo-status-${row.competency_code}`}>
        {isES ? m.es : m.en}
      </Badge>
    );
  };

  const completedHistory = useMemo(() => {
    const done = benchmarks.filter((b) => b.status === 'completed');
    const grouped = new Map();
    done.forEach((b) => {
      const rpid2 = b.snapshot?.role_profile?.id;
      const k = `${b.evaluated_user_id || b.evaluated_name}::${rpid2}`;
      if (!grouped.has(k)) grouped.set(k, []);
      grouped.get(k).push(b);
    });
    return [...grouped.entries()].map(([k, arr]) => ({
      key: k,
      name: arr[0].evaluated_name,
      evaluatedUserId: arr[0].evaluated_user_id,
      profileId: arr[0].snapshot?.role_profile?.id,
      profileTitle: arr[0].snapshot?.role_profile?.position_title,
      items: arr.sort((a, b2) => (b2.updated_at || b2.created_at || '').localeCompare(a.updated_at || a.created_at || '')),
    }));
  }, [benchmarks]);

  const loadTrend = async (g) => {
    try {
      const { data } = await api.get('/admin/rb/trend', {
        params: {
          role_profile_id: g.profileId,
          ...(g.evaluatedUserId ? { evaluated_user_id: g.evaluatedUserId } : { evaluated_name: g.name }),
        },
      });
      setTrend(data);
      setTrendMeta({ name: g.name, profileTitle: g.profileTitle });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Founder-approved Reminder Auto-Start: prefill the EXISTING create flow
  // (employee + Role Profile) from a due reminder. Nothing is created,
  // assigned or submitted until the user confirms the normal create flow.
  const createCardRef = React.useRef(null);
  const startFromReminder = (r) => {
    if (r.evaluated_user_id) setEmpId(r.evaluated_user_id);
    setRpId(r.role_profile_id);
    setEvaluatorId('');
    createCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    toast.info(L('Formulario prellenado desde el recordatorio — revisa competencias y evaluador antes de iniciar.',
      'Form pre-filled from the reminder — review competencies and evaluator before starting.'));
  };

  const trendChart = useMemo(() => {
    if (!trend?.trend_available) return null;
    const codes = [];
    trend.measurements.forEach((m) => m.competencies.forEach((c) => {
      if (!codes.find((x) => x.code === c.competency_code)) {
        codes.push({ code: c.competency_code, label: isES ? c.name_es : c.name });
      }
    }));
    const data = trend.measurements.map((m, idx) => {
      const point = { label: `${fmtDate(m.submitted_at)}`, idx };
      m.competencies.forEach((c) => {
        point[c.competency_code] = c.gap;
        point[`${c.competency_code}__exp`] = c.required_level;
        point[`${c.competency_code}__obs`] = c.observed_level;
      });
      return point;
    });
    const seen = {};
    data.forEach((p) => {
      seen[p.label] = (seen[p.label] || 0) + 1;
    });
    if (Object.values(seen).some((n) => n > 1)) {
      data.forEach((p, i) => { p.label = `${p.label} · ${fmtTime(trend.measurements[i].submitted_at)}`; });
    }
    // A6 executive table: latest vs previous measurement.
    const last = trend.measurements[trend.measurements.length - 1];
    const prev = trend.measurements[trend.measurements.length - 2];
    const prevMap = {};
    (prev?.competencies || []).forEach((c) => { prevMap[c.competency_code] = c; });
    const table = (last?.competencies || []).map((c) => {
      const p = prevMap[c.competency_code];
      const delta = (c.observed_level != null && p?.observed_level != null)
        ? Math.round((c.observed_level - p.observed_level) * 100) / 100 : null;
      return {
        code: c.competency_code, label: isES ? c.name_es : c.name,
        prevObs: p?.observed_level ?? null, curObs: c.observed_level ?? null,
        delta, gap: c.gap ?? null,
        trend: delta == null ? null : (delta > 0 ? 'up' : (delta < 0 ? 'down' : 'flat')),
      };
    });
    const bankLabels = [...new Set(trend.measurements.map((m) => m.bank_label))];
    // Founder-approved Evolution UX: 2 measurements → grouped horizontal bars
    // (fixed 1–5 scale); 3+ measurements → competency selector for the lines.
    const two = trend.measurements.length === 2;
    const barData = two ? (last?.competencies || []).map((c) => {
      const p = prevMap[c.competency_code];
      return {
        code: c.competency_code,
        label: isES ? c.name_es : c.name,
        prev: p?.observed_level ?? null,
        cur: c.observed_level ?? null,
        expected: c.required_level ?? null,
      };
    }) : null;
    return { codes, data, bankLabels, table, prevDate: prev?.submitted_at, lastDate: last?.submitted_at, two, barData };
  }, [trend, isES]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default the competency selector (3+ measurements) to the first competency.
  useEffect(() => {
    if (trendChart && !trendChart.two) {
      setTrendSel(trendChart.codes.length ? [trendChart.codes[0].code] : []);
    } else {
      setTrendSel([]);
    }
  }, [trend]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Topbar
        title={L('Brechas de Competencias', 'Competency Gaps')}
        subtitle={L('TalentCompetency — observación conductual gobernada vs. niveles requeridos del Perfil de Puesto (metodología Esperado vs. Observado, BMIC de CTH)',
          'TalentCompetency — governed behavioral observation vs. Role Profile required levels (Expected vs. Observed methodology, CTH BMIC)')}
      />
      <div className="px-6 lg:px-8 py-4 space-y-4" data-testid="evo-page">
        {!openBm && (
          <>
            <div className="flex justify-end">
              <RequestHMSupport suite="competency_gaps" origin="/client/talentcompetency/evo" />
            </div>
            {/* ------------------------------ Reminders ------------------------------ */}
            <Card className="p-4 shadow-card space-y-3" data-testid="evo-reminders-card">
              <div className="flex flex-wrap items-center gap-3">
                <BellRing className="h-5 w-5 text-primary" />
                <p className="text-sm font-semibold">{L('Recordatorios de reevaluación', 'Re-evaluation reminders')}</p>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{L('Frecuencia', 'Cadence')}</span>
                  <Select value={settings?.cadence || 'semiannual'}
                          onValueChange={(v) => { setSettings((s) => ({ ...s, cadence: v })); if (v !== 'custom') saveSettings(v); }}>
                    <SelectTrigger className="h-8 w-52" data-testid="evo-cadence-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CADENCES.map((c) => <SelectItem key={c.v} value={c.v}>{isES ? c.es : c.en}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {settings?.cadence === 'custom' && (
                    <>
                      <Input className="h-8 w-20" type="number" min="1" max="36" value={customMonths}
                             onChange={(e) => setCustomMonths(e.target.value)} data-testid="evo-custom-months" />
                      <Button size="sm" className="h-8" disabled={!customMonths || Number(customMonths) < 1 || Number(customMonths) > 36}
                              onClick={() => saveSettings('custom', customMonths)} data-testid="evo-save-cadence">
                        {L('Guardar', 'Save')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {reminders.items?.length ? (
                <div className="space-y-1.5" data-testid="evo-reminders-list">
                  {reminders.items.map((r) => (
                    <div key={r.reminder_key} className={`flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm ${r.acknowledged ? 'opacity-60' : ''}`}>
                      <Badge variant="outline" className={`text-xs tabular-nums ${r.days_remaining < 0 ? 'bg-rose-50 text-rose-800 border-rose-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                        {r.days_remaining < 0
                          ? L(`Vencido hace ${Math.abs(r.days_remaining)} días`, `Overdue by ${Math.abs(r.days_remaining)} days`)
                          : L(`En ${r.days_remaining} días`, `In ${r.days_remaining} days`)}
                      </Badge>
                      <span className="font-medium">{r.evaluated_name}</span>
                      <span className="text-muted-foreground">· {r.role_profile_title}</span>
                      <span className="text-xs text-muted-foreground">
                        {L('Última medición', 'Last measured')}: {fmtDate(r.last_evaluated_at)} · {L('Próxima', 'Next due')}: {fmtDate(r.due_date)}
                      </span>
                      {!r.acknowledged && (
                        <span className="ml-auto flex gap-1">
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                                  onClick={() => startFromReminder(r)} data-testid={`evo-reminder-start-${r.benchmark_id}`}>
                            <Plus className="h-3.5 w-3.5" /> {L('Iniciar reevaluación', 'Start re-evaluation')}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => ackReminder(r.reminder_key)}
                                  data-testid={`evo-ack-${r.benchmark_id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {L('Atendido', 'Acknowledge')}
                          </Button>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  {L('Sin reevaluaciones próximas dentro de la frecuencia configurada.', 'No upcoming re-evaluations within the configured cadence.')}
                </p>
              )}
            </Card>

            {/* ------------------------------ Create ------------------------------ */}
            <Card ref={createCardRef} className="p-4 shadow-card space-y-3" data-testid="evo-create-card">
              <p className="text-sm font-semibold">{L('Iniciar medición de Brechas de Competencias', 'Start a Competency Gaps measurement')}</p>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{L('Colaborador/a a evaluar', 'Employee to evaluate')}</Label>
                  <Select value={empId} onValueChange={setEmpId}>
                    <SelectTrigger className="h-10 w-full" data-testid="evo-employee-select">
                      <SelectValue placeholder={L('Seleccionar colaborador/a…', 'Select an employee…')} />
                    </SelectTrigger>
                    <SelectContent className="max-w-[30rem]">
                      {employees.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <span className="flex flex-col text-left py-0.5">
                            <span className="text-sm leading-snug">{p.name}</span>
                            {p.position && <span className="text-xs text-muted-foreground whitespace-normal leading-snug">{p.position}</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{L('Perfil de Puesto aprobado', 'Approved Role Profile')}</Label>
                  <Select value={rpId} onValueChange={(v) => { setRpId(v); setAutoProfileHint(false); }}>
                    <SelectTrigger className="h-10 w-full" data-testid="evo-profile-select">
                      <SelectValue placeholder={L('Seleccionar perfil…', 'Select a profile…')} />
                    </SelectTrigger>
                    <SelectContent className="max-w-[30rem]">
                      {profiles.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <span className="flex flex-col text-left py-0.5">
                            <span className="whitespace-normal text-sm leading-snug">{p.position_title}</span>
                            <span className="text-xs text-muted-foreground leading-snug">
                              {(p.requirements || []).length} {L('competencias', 'competencies')}{p.scope === 'global' ? L(' · Plantilla global', ' · Global template') : L(' · De tu empresa', ' · Company-specific')}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {autoProfileHint && (
                    <p className="text-xs text-teal-700" data-testid="evo-profile-auto-hint">
                      {L('Preseleccionado desde la posición formal del colaborador — puedes cambiarlo.',
                        'Preselected from the collaborator\u2019s formal position — you can change it.')}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{L('Evaluador/a', 'Evaluator')}</Label>
                  <Select value={evaluatorId} onValueChange={setEvaluatorId}>
                    <SelectTrigger className="h-10 w-full" data-testid="evo-evaluator-select">
                      <SelectValue placeholder={L('Seleccionar evaluador/a…', 'Select an evaluator…')} />
                    </SelectTrigger>
                    <SelectContent>
                      {evaluators.length === 0 && (
                        <p className="px-3 py-2 text-xs text-muted-foreground" data-testid="evo-evaluator-empty">
                          {L('No hay evaluadores elegibles activos.', 'No active eligible evaluators.')}
                        </p>
                      )}
                      {evaluators.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}{p.designated ? ` · ${L('Evaluador designado', 'Designated evaluator')}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="evo-evaluator-hint">
                  {L('Gobernanza: pueden evaluar los administradores del portal, el personal de CTH y los Evaluadores Designados autorizados para esta persona.',
                    'Governance: portal administrators, CTH staff and Designated Evaluators authorized for this person can evaluate.')}
                  {' '}
                  <button type="button" className="underline text-primary" onClick={() => navigate('/client/talentcompetency/evaluadores')} data-testid="evo-manage-evaluators">
                    {L('Gestionar evaluadores designados', 'Manage designated evaluators')}
                  </button>
                </p>

              {selectedProfile && (
                <div className="space-y-2" data-testid="evo-competency-picker">
                  <p className="text-xs text-muted-foreground">
                    {L(`Competencias a observar: ${selectedCodes.length} de ${profileCodes.length} del perfil seleccionadas (máximo 12 por medición)`,
                      `Competencies to observe: ${selectedCodes.length} of ${profileCodes.length} in the profile selected (maximum 12 per measurement)`)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {profileCodes.map((code) => {
                      const on = selectedCodes.includes(code);
                      return (
                        <button key={code} type="button" onClick={() => toggleCode(code)}
                                data-testid={`evo-comp-chip-${code}`}
                                className={`min-h-8 px-3 py-1 rounded-full border text-xs leading-snug transition
                                  ${on ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted/50'}
                                  ${!on && selectedCodes.length >= 12 ? 'opacity-50' : ''}`}>
                          {compLabel(code)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button size="sm" onClick={create}
                        disabled={busy || !empId || !rpId || !evaluatorId || !selectedCodes.length}
                        data-testid="evo-create-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> {L('Iniciar evaluación', 'Start evaluation')}</>}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {L('La persona evaluada NO responde esta evaluación: la completa el evaluador designado. Sin puntaje global de idoneidad por diseño.',
                    'The evaluated employee does NOT answer this evaluation: the designated evaluator completes it. No overall fitness score by design.')}
                </p>
              </div>
            </Card>

            {/* ------------------------------ List ------------------------------ */}
            <Card className="shadow-card" data-testid="evo-list-card">
              <Table containerClassName="!overflow-visible">
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead>{L('Evaluado/a', 'Evaluated')}</TableHead>
                    <TableHead>{L('Perfil', 'Profile')}</TableHead>
                    <TableHead>{L('Evaluador/a', 'Evaluator')}</TableHead>
                    <TableHead>{L('Indicadores', 'Indicators')}</TableHead>
                    <TableHead>{L('Estado', 'Status')}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {benchmarks.map((b) => (
                    <TableRow key={b.id} data-testid={`evo-row-${b.id}`}>
                      <TableCell className="font-medium">{b.evaluated_name}</TableCell>
                      <TableCell className="text-sm">{b.snapshot?.role_profile?.position_title}</TableCell>
                      <TableCell className="text-sm">{b.evaluator_name || '—'}</TableCell>
                      <TableCell className="text-xs tabular-nums">{(b.snapshot?.item_ids || []).length}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${b.status === 'completed' ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                          : b.status === 'cancelled' ? 'bg-slate-100 text-slate-500 border-slate-300 line-through' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                          {b.status === 'completed' ? L('Completada', 'Completed')
                            : b.status === 'cancelled' ? L('Cancelada', 'Cancelled') : L('Pendiente del evaluador', 'Pending evaluator')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {b.status !== 'cancelled' && (
                          <Button size="sm" variant="outline" onClick={() => openBenchmark(b.id)} data-testid={`evo-open-${b.id}`}>
                            {b.status === 'completed' ? L('Ver resultados', 'View results')
                              : (b.evaluator_user_id === user?.id ? L('Responder', 'Answer') : L('Ver estado', 'View status'))}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!benchmarks.length && (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground italic">
                      {L('Aún no hay mediciones de Brechas de Competencias.', 'No Competency Gaps measurements yet.')}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>

            {/* ------------------------------ History ------------------------------ */}
            {completedHistory.length > 0 && (
              <Card className="p-4 shadow-card space-y-3" data-testid="evo-history-card">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">{L('Historial de mediciones', 'Measurement history')}</p>
                </div>
                {completedHistory.map((g) => (
                  <div key={g.key} className="text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">
                        {g.name} <span className="text-xs text-muted-foreground">· {g.profileTitle} · {g.items.length} {L('medición(es)', 'measurement(s)')}</span>
                      </p>
                      {g.items.length >= 2 ? (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => loadTrend(g)}
                                data-testid={`evo-trend-btn-${g.key}`}>
                          <TrendingUp className="h-3.5 w-3.5" /> {L('Ver evolución', 'View trend')}
                        </Button>
                      ) : (
                        <span className="text-[11px] text-muted-foreground italic">
                          {L('Se requieren al menos dos mediciones para mostrar evolución.', 'At least two measurements are required to show a trend.')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {(() => {
                        const dates = g.items.map((b) => fmtDate(b.updated_at || b.created_at));
                        const dup = new Set(dates.filter((d, i) => dates.indexOf(d) !== i));
                        return g.items.map((b, i) => (
                          <button key={b.id} type="button" onClick={() => openBenchmark(b.id)}
                                  data-testid={`evo-history-${b.id}`}
                                  className="h-7 px-2.5 rounded-md border border-border text-xs hover:bg-muted/50">
                            {dates[i]}{dup.has(dates[i]) ? ` · ${fmtTime(b.updated_at || b.created_at)}` : ''}
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                ))}
              </Card>
            )}

            {/* ------------------------ Gap trend (longitudinal) ------------------------ */}
            {trend && trendMeta && (
              <Card className="p-4 shadow-card space-y-3" data-testid="evo-trend-card">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">
                    {L('Evolución de brechas', 'Gap evolution')} — {trendMeta.name} · {trendMeta.profileTitle}
                  </p>
                  <Button size="sm" variant="ghost" className="ml-auto h-7 w-7 p-0" onClick={() => { setTrend(null); setTrendMeta(null); }}
                          data-testid="evo-trend-close">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {!trend.trend_available ? (
                  <p className="text-sm text-muted-foreground italic" data-testid="evo-trend-insufficient">
                    {L('Se requieren al menos dos mediciones para mostrar evolución.', 'At least two measurements are required to show a trend.')}
                  </p>
                ) : (
                  <>
                    {/* Executive comparison table FIRST (emphasized by design) */}
                    <div data-testid="evo-trend-table">
                      <Table containerClassName="!overflow-visible">
                        <TableHeader className="bg-muted/40">
                          <TableRow>
                            <TableHead>{L('Competencia', 'Competency')}</TableHead>
                            <TableHead className="text-center">{L('Medición anterior', 'Prior measurement')} {trendChart.prevDate && <span className="block text-[10px] font-normal">{fmtDate(trendChart.prevDate)}</span>}</TableHead>
                            <TableHead className="text-center">{L('Medición actual', 'Current measurement')} {trendChart.lastDate && <span className="block text-[10px] font-normal">{fmtDate(trendChart.lastDate)}</span>}</TableHead>
                            <TableHead className="text-center">{L('Variación vs. anterior', 'Change vs. prior')}</TableHead>
                            <TableHead className="text-center">{L('Brecha actual vs. esperado', 'Current gap vs. expected')}</TableHead>
                            <TableHead>{L('Tendencia', 'Trend')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {trendChart.table.map((r) => (
                            <TableRow key={r.code} data-testid={`evo-trend-row-${r.code}`}>
                              <TableCell className="font-medium text-sm">{r.label}</TableCell>
                              <TableCell className="text-center tabular-nums">{r.prevObs ?? '—'}</TableCell>
                              <TableCell className="text-center tabular-nums">{r.curObs ?? '—'}</TableCell>
                              <TableCell className={`text-center tabular-nums font-semibold ${r.delta > 0 ? 'text-emerald-700' : r.delta < 0 ? 'text-rose-700' : ''}`}>
                                {r.delta == null ? '—' : `${r.delta > 0 ? '+' : ''}${r.delta}`}
                              </TableCell>
                              <TableCell className="text-center tabular-nums">{r.gap ?? '—'}</TableCell>
                              <TableCell className="text-sm">
                                {r.trend === 'up' && <span className="text-emerald-700">↑ {L('mejora', 'improving')}</span>}
                                {r.trend === 'down' && <span className="text-rose-700">↓ {L('deterioro', 'deteriorating')}</span>}
                                {r.trend === 'flat' && <span className="text-muted-foreground">→ {L('estable', 'stable')}</span>}
                                {r.trend == null && <span className="text-muted-foreground italic text-xs">{L('evidencia insuficiente', 'insufficient evidence')}</span>}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {trendChart.two ? (
                      /* 2 measurements — grouped horizontal bars, fixed 1–5 scale */
                      <div className="space-y-1" data-testid="evo-trend-bars">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {L('Comparación de niveles observados (escala 1–5)', 'Observed level comparison (1–5 scale)')}
                        </p>
                        <div style={{ height: Math.max(180, trendChart.barData.length * 52 + 70) }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={trendChart.barData} layout="vertical"
                                      margin={{ top: 8, right: 24, left: 8, bottom: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5eaef" horizontal={false} />
                              <XAxis type="number" domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} />
                              <YAxis type="category" dataKey="label" width={200} tick={{ fontSize: 11 }} />
                              <Tooltip formatter={(v) => (v == null ? '—' : v)} />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <Bar dataKey="prev" name={`${L('Medición anterior', 'Prior measurement')} · ${fmtDate(trendChart.prevDate)}`}
                                   fill="#94a3b8" radius={[0, 3, 3, 0]} barSize={10} />
                              <Bar dataKey="cur" name={`${L('Medición actual', 'Current measurement')} · ${fmtDate(trendChart.lastDate)}`}
                                   fill="#0f3d5c" radius={[0, 3, 3, 0]} barSize={10} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ) : (
                      /* 3+ measurements — competency selector, only selected trends plotted */
                      <div className="space-y-2" data-testid="evo-trend-multi">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {L('Evolución por competencia — seleccione hasta 4 para graficar (la tabla ejecutiva muestra todas)',
                             'Trend by competency — select up to 4 to plot (the executive table shows all)')}
                        </p>
                        <div className="flex flex-wrap gap-1.5" data-testid="evo-trend-selector">
                          {trendChart.codes.map((c) => {
                            const on = trendSel.includes(c.code);
                            return (
                              <button key={c.code} type="button" data-testid={`evo-trend-sel-${c.code}`}
                                      onClick={() => setTrendSel((s) => on
                                        ? s.filter((x) => x !== c.code)
                                        : (s.length >= 4 ? s : [...s, c.code]))}
                                      className={`h-7 px-2.5 rounded-full border text-xs transition
                                        ${on ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted/50'}
                                        ${!on && trendSel.length >= 4 ? 'opacity-50' : ''}`}>
                                {c.label}
                              </button>
                            );
                          })}
                        </div>
                        {trendSel.length === 0 ? (
                          <p className="text-sm text-muted-foreground italic" data-testid="evo-trend-empty-selection">
                            {L('Seleccione al menos una competencia para visualizar su evolución.',
                               'Select at least one competency to plot its trend.')}
                          </p>
                        ) : (
                          <div className="h-64" data-testid="evo-trend-chart">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={trendChart.data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5eaef" />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} domain={[-2, 4]} allowDataOverflow
                                       label={{ value: L('Brecha', 'Gap'), angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                                <ReferenceLine y={0} stroke="#1a7f4e" strokeDasharray="4 3" />
                                <Tooltip formatter={(v, name) => [`${L('Brecha vs. esperado', 'Gap vs. expected')}: ${v}`, name]} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                {trendChart.codes.filter((c) => trendSel.includes(c.code)).map((c, i) => (
                                  <Line key={c.code} type="monotone" dataKey={c.code} name={c.label}
                                        stroke={TREND_COLORS[i % TREND_COLORS.length]} strokeWidth={2}
                                        dot={{ r: 3 }} connectNulls={false} />
                                ))}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {L('Brecha = Esperado − Observado en cada medición (menor es mejor; 0 = cumple). Cada punto usa el nivel esperado y el banco de indicadores vigentes en su fecha (instantáneas inmutables): ',
                        'Gap = Expected − Observed at each measurement (lower is better; 0 = meets). Each point uses the expected level and indicator bank pinned at its date (immutable snapshots): ')}
                      {trendChart.bankLabels.join(' · ')}
                      {trendChart.bankLabels.length > 1 && (
                        <b>{L(' — las mediciones usan versiones distintas del banco; compare con criterio.', ' — measurements use different bank versions; compare with judgment.')}</b>
                      )}
                      {'. '}
                      {L('Los tramos sin punto corresponden a evidencia insuficiente. Sin puntaje global ni interpretación predictiva por diseño.',
                        'Missing points correspond to insufficient evidence. No global score and no predictive interpretation by design.')}
                    </p>
                  </>
                )}
              </Card>
            )}
          </>
        )}

        {openBm && (
          <>
            <Button variant="ghost" size="sm" onClick={() => { setOpenBm(null); setResults(null); }} data-testid="evo-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> {L('Volver', 'Back')}
            </Button>
            <Card className="p-4 shadow-card text-sm flex flex-wrap items-center gap-x-6 gap-y-1" data-testid="evo-detail-header">
              <span><b>{L('Evaluado/a', 'Evaluated')}:</b> {openBm.evaluated_name}</span>
              <span><b>{L('Perfil', 'Profile')}:</b> {openBm.snapshot.role_profile.position_title}</span>
              <span><b>{L('Evaluador/a', 'Evaluator')}:</b> {openBm.evaluator_name || '—'}</span>
              <span className="tabular-nums"><b>{L('Indicadores', 'Indicators')}:</b> {totalItems}</span>
              {openBm.status === 'open' && !openBm.response && (
                <Button size="sm" variant="outline" className="ml-auto h-8 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                        onClick={() => setShowCancel(true)} data-testid="evo-cancel-btn">
                  {L('Cancelar evaluación', 'Cancel evaluation')}
                </Button>
              )}
            </Card>

            {showCancel && (
              <Card className="p-4 shadow-card space-y-3 border-rose-200" data-testid="evo-cancel-dialog">
                <p className="text-sm font-semibold">{L('Cancelar esta evaluación pendiente', 'Cancel this pending evaluation')}</p>
                <p className="text-xs text-muted-foreground">
                  {L('La cancelación queda registrada en auditoría (quién, cuándo y por qué). No se elimina evidencia: la evaluación pasa a estado Cancelada y no cuenta como medición histórica.',
                    'Cancellation is audited (who, when, why). No evidence is deleted: the evaluation becomes Cancelled and never counts as a historical measurement.')}
                </p>
                <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                       placeholder={L('Motivo de cancelación (obligatorio)…', 'Cancellation reason (required)…')}
                       data-testid="evo-cancel-reason" />
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive" disabled={busy || !cancelReason.trim()}
                          onClick={cancelEvaluation} data-testid="evo-cancel-confirm">
                    {L('Confirmar cancelación', 'Confirm cancellation')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowCancel(false); setCancelReason(''); }}>
                    {L('Volver', 'Back')}
                  </Button>
                </div>
              </Card>
            )}

            {!results && !iAmEvaluator && (
              <Card className="p-6 shadow-card text-sm text-muted-foreground" data-testid="evo-pending-note">
                <Target className="h-5 w-5 text-primary mb-2" />
                {L(`Esta observación conductual está pendiente del evaluador designado (${openBm.evaluator_name || '—'}). Recibirás los resultados aquí cuando la complete.`,
                  `This behavioral observation is pending the designated evaluator (${openBm.evaluator_name || '—'}). Results will appear here once submitted.`)}
              </Card>
            )}

            {!results && iAmEvaluator && (
              <Card className="p-4 shadow-card space-y-5" data-testid="evo-answer-form">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">{L('Observación conductual', 'Behavioral observation')}</p>
                  <span className="text-xs text-muted-foreground tabular-nums">{answered}/{totalItems}</span>
                </div>
                <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="evo-scale-legend">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                    {L('Escala de observación conductual', 'Behavioral observation scale')}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                    {[1, 2, 3, 4, 5].map((v) => (
                      <p key={v}><b className="tabular-nums mr-1">{v}</b>— {isES ? SCALE[v].es : SCALE[v].en}</p>
                    ))}
                    <p><b className="mr-1">N/E</b>— {L('No tengo evidencia suficiente (excluida del promedio)', "I don't have sufficient evidence (excluded from the mean)")}</p>
                  </div>
                </div>
                {itemsByComp.map(([code, items]) => (
                  <div key={code} className="space-y-2">
                    <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                      {compName(code)} — {L('Indicadores Conductuales', 'Behavioral Indicators')}
                    </p>
                    {items.map((it) => (
                      <div key={it.item_id} className="rounded-md border border-border p-3" data-testid={`evo-item-${it.item_id}`}>
                        {(it.focus_es || it.focus_en) && (
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-0.5">{isES ? it.focus_es : it.focus_en}</p>
                        )}
                        <p className="text-sm mb-2">{isES ? it.text_es : it.text_en}</p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <button key={v} type="button" data-testid={`evo-ans-${it.item_id}-${v}`}
                                    title={isES ? SCALE[v].es : SCALE[v].en}
                                    onClick={() => setAnswers((a) => ({ ...a, [it.item_id]: v }))}
                                    className={`h-8 w-8 rounded-md border text-sm font-medium transition
                                      ${answers[it.item_id] === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted/50'}`}>
                              {v}
                            </button>
                          ))}
                          <button type="button" data-testid={`evo-ans-${it.item_id}-noev`}
                                  onClick={() => setAnswers((a) => ({ ...a, [it.item_id]: 'no_evidence' }))}
                                  className={`h-8 px-2.5 rounded-md border text-xs transition flex items-center gap-1
                                    ${answers[it.item_id] === 'no_evidence' ? 'bg-slate-700 text-white border-slate-700' : 'border-dashed border-border text-muted-foreground hover:bg-muted/50'}`}>
                            <CircleHelp className="h-3 w-3" />
                            {L('No tengo evidencia suficiente', "I don't have sufficient evidence")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <Button onClick={submit} disabled={busy || answered < totalItems} data-testid="evo-submit-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L('Enviar observación', 'Submit observation')}
                </Button>
              </Card>
            )}

            {results && (
              <Card className="shadow-card" data-testid="evo-results">
                <div className="p-4 border-b border-border text-sm flex flex-wrap items-center gap-x-6 gap-y-2">
                  <span><b>{L('Evaluador/a', 'Evaluator')}:</b> {results.evaluator_name}</span>
                  <span><b>{L('Fecha', 'Date')}:</b> {fmtDate(results.submitted_at)}</span>
                  {results.previous_evaluation && (
                    <span className="text-xs text-muted-foreground">
                      {L('Medición anterior', 'Prior measurement')}: {fmtDate(results.previous_evaluation.submitted_at)}
                    </span>
                  )}
                  <span className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => downloadReport('pdf')} data-testid="evo-export-pdf">
                      <Download className="h-3.5 w-3.5" /> PDF
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => downloadReport('docx')} data-testid="evo-export-docx">
                      <Download className="h-3.5 w-3.5" /> Word
                    </Button>
                  </span>
                </div>
                <Table containerClassName="!overflow-visible">
                  <TableHeader className="bg-muted/40">
                    <TableRow>
                      <TableHead>{L('Competencia', 'Competency')}</TableHead>
                      <TableHead className="text-center">{L('Esperado', 'Expected')}</TableHead>
                      <TableHead className="text-center">{L('Observado', 'Observed')}</TableHead>
                      <TableHead className="text-center">{L('Brecha vs. esperado', 'Gap vs. expected')}</TableHead>
                      <TableHead>{L('Estado', 'Status')}</TableHead>
                      <TableHead className="text-center">{L('Evidencia', 'Evidence')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.results.map((r) => (
                      <React.Fragment key={r.competency_code}>
                      <TableRow data-testid={`evo-result-${r.competency_code}`} className="cursor-pointer"
                                onClick={() => setOpenDetail(openDetail === r.competency_code ? null : r.competency_code)}>
                        <TableCell className="font-medium text-sm">
                          {isES ? r.name_es : r.name}
                          {r.previous_observed != null && (
                            <span className="block text-[11px] text-muted-foreground mt-0.5">
                              {L('Variación vs. medición anterior', 'Change vs. prior measurement')}:
                              {r.delta_vs_previous != null ? (
                                <b className={r.delta_vs_previous >= 0 ? ' text-emerald-700' : ' text-rose-700'}>
                                  {' '}{r.delta_vs_previous > 0 ? '+' : ''}{r.delta_vs_previous}
                                </b>
                              ) : ' —'}
                              {' '}({L('anterior', 'prior')}: {r.previous_observed})
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center tabular-nums">{r.required_level}</TableCell>
                        <TableCell className="text-center tabular-nums" data-testid={`evo-observed-${r.competency_code}`}>
                          {r.observed_level ?? '—'}
                        </TableCell>
                        <TableCell className="text-center tabular-nums" data-testid={`evo-gap-${r.competency_code}`}>
                          {r.gap ?? '—'}
                        </TableCell>
                        <TableCell>{statusBadge(r)}</TableCell>
                        <TableCell className="text-center tabular-nums text-xs">
                          {r.evidence_count}/{r.evidence_total}
                        </TableCell>
                      </TableRow>
                      {openDetail === r.competency_code && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={6}>
                            <div className="space-y-1.5 py-1" data-testid={`evo-drill-${r.competency_code}`}>
                              {(r.indicators || []).map((i) => (
                                <div key={i.item_id} className="text-xs flex items-start gap-2">
                                  <Badge variant="outline" className="shrink-0 tabular-nums">
                                    {i.value === 'no_evidence' ? 'N/E' : i.value}
                                  </Badge>
                                  <span>
                                    {(i.focus_es || i.focus_en) && <b className="mr-1">{isES ? i.focus_es : i.focus_en}:</b>}
                                    {isES ? i.text_es : i.text_en}
                                  </span>
                                </div>
                              ))}
                              {(r.status === 'yellow' || r.status === 'red') && (
                                handoffByCode[r.competency_code] ? (
                                  <Badge variant="outline" className="mt-1 text-xs bg-teal-100 text-teal-800 border-teal-300"
                                         data-testid={`evo-handoff-done-${r.competency_code}`}>
                                    <GraduationCap className="h-3 w-3 mr-1" /> {L('En TalentTrain', 'In TalentTrain')}
                                  </Badge>
                                ) : (
                                <Button size="sm" variant="outline" className="mt-1 h-7 text-xs" data-testid={`evo-handoff-${r.competency_code}`}
                                        onClick={async (ev) => {
                                          ev.stopPropagation();
                                          try {
                                            const { data: hd } = await api.post(`/admin/rb/benchmarks/${results.benchmark_id}/handoff/${r.competency_code}`, {});
                                            if (hd.already_exists) {
                                              toast.info(L('Esta brecha ya estaba en TalentTrain — no se duplicó.', 'This gap was already in TalentTrain — not duplicated.'));
                                            } else {
                                              toast.success(L('Necesidad de desarrollo enviada a TalentTrain (sin inscripción automática)', 'Development need handed off to TalentTrain (no automatic enrollment)'));
                                            }
                                            refreshHandoffs(results.benchmark_id);
                                          } catch (e2) {
                                            const dd = e2?.response?.data?.detail;
                                            toast.error(typeof dd === 'string' ? dd : 'Error');
                                          }
                                        }}>
                                  {L('Enviar a TalentTrain', 'Send to TalentTrain')}
                                </Button>
                                )
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
                <p className="p-4 text-xs text-muted-foreground border-t border-border">
                  {L('Resultados por competencia. Por diseño no existe un puntaje global de idoneidad ni una conclusión de apto/no apto.',
                    'Competency-level results. By design there is no overall suitability score and no fit/unfit conclusion.')}
                </p>
              </Card>
            )}

            {results && (() => {
              const prios = results.results
                .filter((r) => r.status === 'red' || r.status === 'yellow')
                .sort((a, b) => (a.status === b.status ? (b.gap || 0) - (a.gap || 0) : (a.status === 'red' ? -1 : 1)));
              return (
                <Card className="p-4 shadow-card space-y-3" data-testid="evo-priorities-card">
                  <p className="text-sm font-semibold">{L('Prioridades de desarrollo', 'Development priorities')}</p>
                  {!prios.length ? (
                    <p className="text-sm text-muted-foreground italic">
                      {L('No se identificaron brechas de desarrollo en esta medición.', 'No development gaps were identified in this measurement.')}
                    </p>
                  ) : (
                    <>
                      <div className="space-y-1.5">
                        {prios.map((r) => {
                          const h = handoffByCode[r.competency_code];
                          return (
                          <label key={r.competency_code} className={`flex items-start gap-2 rounded-md border p-2.5 text-sm ${h ? 'border-teal-200 bg-teal-50/40' : 'border-border cursor-pointer hover:bg-muted/30'}`}
                                 data-testid={`evo-priority-${r.competency_code}`}>
                            <input type="checkbox" className="mt-0.5" checked={prioritySel.includes(r.competency_code)}
                                   disabled={!!h}
                                   onChange={() => setPrioritySel((s) => s.includes(r.competency_code)
                                     ? s.filter((c) => c !== r.competency_code) : [...s, r.competency_code])}
                                   data-testid={`evo-priority-check-${r.competency_code}`} />
                            <span className="min-w-0 flex-1">
                              <b>{isES ? r.name_es : r.name}</b> {statusBadge(r)}
                              {h && (
                                <Badge variant="outline" className="ml-1.5 text-xs bg-teal-100 text-teal-800 border-teal-300"
                                       data-testid={`evo-in-tt-${r.competency_code}`}>
                                  <GraduationCap className="h-3 w-3 mr-1" /> {L('En TalentTrain', 'In TalentTrain')}
                                </Badge>
                              )}
                              <span className="block text-xs text-muted-foreground mt-0.5">
                                {L('Brecha vs. esperado', 'Gap vs. expected')}: <b>{r.gap}</b> · {L('esperado', 'expected')} {r.required_level} / {L('observado', 'observed')} {r.observed_level}
                                {r.delta_vs_previous != null && r.delta_vs_previous < 0 && (
                                  <b className="text-rose-700"> · {L('deterioro vs. medición anterior', 'deterioration vs. prior measurement')} ({r.delta_vs_previous})</b>
                                )}
                                {h && (
                                  <span className="block mt-0.5 text-teal-800">
                                    {L('Transferida el', 'Transferred on')} {fmtDate(h.created_at)} · {L('registro histórico inmutable', 'immutable historical record')}
                                  </span>
                                )}
                              </span>
                            </span>
                            {h && openBm?.evaluated_user_id && (
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-teal-800 shrink-0 gap-1"
                                      data-testid={`evo-view-in-tt-${r.competency_code}`}
                                      onClick={(ev) => { ev.preventDefault(); navigate(`/client/talenttrain/${openBm.evaluated_user_id}`); }}>
                                <ExternalLink className="h-3 w-3" /> {L('Ver en TalentTrain', 'View in TalentTrain')}
                              </Button>
                            )}
                          </label>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-3">
                        <Button size="sm" disabled={busy || !prioritySel.length} onClick={createHandoffs} data-testid="evo-handoff-batch-btn">
                          {L('Crear plan en TalentTrain', 'Create plan in TalentTrain')}
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          {L('Traslado gobernado: requiere esta acción humana y NUNCA inscribe automáticamente — TalentTrain gobierna la acción de desarrollo.',
                            'Governed handoff: requires this human action and NEVER auto-enrolls — TalentTrain governs the development action.')}
                        </p>
                      </div>
                    </>
                  )}
                </Card>
              );
            })()}
          </>
        )}
      </div>
    </>
  );
}
